'use client';

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from 'react';

/**
 * Personal bests for the /fun arcade live in localStorage rather than the
 * database: they are throwaway scores, they must survive a logged-out visit,
 * and nobody should be able to cheat their way onto a real leaderboard with a
 * devtools console. We read them through useSyncExternalStore so a write in one
 * component (a finished game) repaints every other reader (the hub card).
 */
const BEST_UPDATED = 'factiii:fun:best-updated';
const subscribe = (onStoreChange: () => void) => {
  // 'storage' only fires in *other* tabs, so same-tab writes announce
  // themselves with the custom event.
  window.addEventListener('storage', onStoreChange);
  window.addEventListener(BEST_UPDATED, onStoreChange);
  return () => {
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener(BEST_UPDATED, onStoreChange);
  };
};
const readBest = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Safari private mode throws on access rather than returning null.
    return null;
  }
};

/** Server snapshot: there is no localStorage during SSR, so nothing is stored. */
const serverSnapshot = () => null;
export const useLocalBest = key => {
  const $ = _c(2);
  let t0;
  if ($[0] !== key) {
    t0 = () => readBest(key);
    $[0] = key;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useSyncExternalStore(subscribe, t0, serverSnapshot);
};

/**
 * Store `value` if it beats what is already there. `direction` decides what
 * "beats" means — Spend the Treasury wants the *lowest* leftover balance, every
 * other game wants the highest number.
 */
export const recordBest = (key: string, value: number, direction: 'higher' | 'lower' = 'higher') => {
  const previous = readBest(key);
  const previousValue = previous === null ? null : Number(previous);
  const hasPrevious = previousValue !== null && !Number.isNaN(previousValue);
  if (hasPrevious) {
    const improved = direction === 'higher' ? value > previousValue : value < previousValue;
    if (!improved) return false;
  }
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Nothing to do if storage is unavailable — the score just isn't kept.
  }
  window.dispatchEvent(new Event(BEST_UPDATED));
  return true;
};